// The main landing screen where users stage files for evaluation.
// Supports drag-and-drop and click-to-select.
// Files are staged in a list before submission so users can
// add multiple papers one at a time.

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import type { Theme, TierKey } from '../types';
import { TIERS } from '../lib/constants';
import ThemeToggle from './ThemeToggle';
import UsageLimitBanner from './UsageLimitBanner';
import dropStyles from '../styles/DropZone.module.css';

interface DropZoneProps {
  onFiles: (files: File[]) => void;
  tier: TierKey;
  used: number;
  canEvaluate: boolean;
  onUpgradeClick: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

export default function DropZone({
  onFiles,
  tier,
  used,
  canEvaluate,
  onUpgradeClick,
  theme,
  onToggleTheme,
}: DropZoneProps) {
  const [staged, setStaged] = useState<File[]>([]);
  const batchLimit = TIERS[tier].batchLimit;
  const monthlyLimit = TIERS[tier].monthlyLimit;

  const onDrop = useCallback((accepted: File[]) => {
    setStaged(prev => {
      const existing = new Set(prev.map(f => f.name));
      const newFiles = accepted.filter(f => !existing.has(f.name));
      // Respect batch limit — don't add more than allowed at once
      const combined = [...prev, ...newFiles];
      return batchLimit === Infinity
        ? combined
        : combined.slice(0, batchLimit as number);
    });
  }, [batchLimit]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: batchLimit === Infinity ? 20 : batchLimit as number,
    disabled: !canEvaluate,
  });

  const removeFile = (name: string) => {
    setStaged(prev => prev.filter(f => f.name !== name));
  };

  return (
    <div className={dropStyles.page}>
      <div className={dropStyles.hero}>
        <h1 className={dropStyles.title}>Desk Reject</h1>
        <p className={dropStyles.tagline}>
          Drop in a paper. Find out if it's worth your time before you spend it.
        </p>
      </div>

      <UsageLimitBanner
        used={used}
        limit={monthlyLimit}
        tier={tier}
        onUpgradeClick={onUpgradeClick}
      />

      <div
        {...getRootProps()}
        className={`${dropStyles.dropZone} ${isDragActive ? dropStyles.dropZoneActive : ''} ${!canEvaluate ? dropStyles.dropZoneDisabled : ''}`}
      >
        <input {...getInputProps()} />
        <span className={dropStyles.dropIcon}>📄</span>
        <p className={dropStyles.dropText}>
          {!canEvaluate
            ? "You've used all your evaluations this month"
            : isDragActive
            ? "Drop it like it's hot"
            : 'Drag PDFs here, or click to select'}
        </p>
        <p className={dropStyles.dropHint}>
          {canEvaluate
            ? batchLimit === Infinity
              ? 'Up to 20 papers at once'
              : `Up to ${batchLimit} paper${batchLimit === 1 ? '' : 's'} at once on your current plan`
            : 'Upgrade to continue evaluating papers'}
        </p>
      </div>

      {staged.length > 0 && (
        <div className={dropStyles.staged}>
          <div className={dropStyles.stagedHeader}>
            <span className={dropStyles.stagedCount}>
              {staged.length} paper{staged.length !== 1 ? 's' : ''} ready
            </span>
            <button
              className={dropStyles.stagedClear}
              onClick={() => setStaged([])}
            >
              Clear all
            </button>
          </div>

          {staged.map(f => (
            <div key={f.name} className={dropStyles.stagedRow}>
              <span className={dropStyles.stagedName}>{f.name}</span>
              <button
                className={dropStyles.stagedRemove}
                onClick={e => { e.stopPropagation(); removeFile(f.name); }}
              >
                ✕
              </button>
            </div>
          ))}

          <button
            className={dropStyles.btnEvaluate}
            onClick={() => onFiles(staged)}
          >
            Evaluate {staged.length} paper{staged.length !== 1 ? 's' : ''} →
          </button>
        </div>
      )}

      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </div>
  );
}